<script>
import sizeMixin from "./sizeMixin";
import DashTip from "./DashTip";
import { squarify } from "./scale";
import { CYCLES, STATUSES, fmtNum, compareSequence } from "@/data/dashboardModel";

// Mosaico de ítems de la Res. 0312: una celda por ítem, área proporcional a su peso en
// puntos, agrupado por ciclo PHVA. Color por estado (Aprobado teal-700, Pendiente teal-300,
// No cumplido rojo, Sin iniciar gris con textura a 45°). Cada celda es un enlace enfocable;
// hover y foco muestran el mismo tooltip.
const GROUP_GAP = 8;
const LABEL_H = 20;
const CELL_GAP = 2;
const MIN_CELL = 24;
const GRID_BELOW = 560;
const GRID_CELL = 44;
const GRID_GAP = 4;

export default {
  name: "ItemMosaic",
  components: { DashTip },
  mixins: [sizeMixin],
  props: {
    items: { type: Array, default: () => [] },
    ready: { type: Boolean, default: false },
    to: { type: [String, Object], default: "/forms/estandares" },
  },
  data() {
    return { active: null, tip: { x: 0, y: 0 }, roving: 0 };
  },
  computed: {
    W() {
      return this.width || 700;
    },
    // Escritorio: treemap proporcional al peso. Si una celda queda por debajo del mínimo táctil
    // (WCAG 2.5.8, 24 px) se prueba con más alto; si aun así no cabe, o en pantallas estrechas,
    // se pasa a cuadrícula de celdas iguales de 44 px agrupadas por ciclo.
    layout() {
      const raw = CYCLES.map((c) => {
        const its = this.items
          .filter((i) => i.cycle === c.key)
          .map((i) => ({ ...i, value: i.value > 0 ? i.value : 0.25 }))
          .sort((a, b) => b.value - a.value || compareSequence(a.sequence, b.sequence));
        return { key: c.key, label: c.label, items: its, value: its.reduce((s, i) => s + i.value, 0) };
      }).filter((g) => g.items.length);
      if (this.W >= GRID_BELOW) {
        const base = Math.round(Math.min(340, this.W * 0.42));
        // Si el área media por ítem ya no da para una celda mínima, ningún treemap cabe: directo a cuadrícula
        const n = raw.reduce((t, g) => t + g.items.length, 0);
        if (!n || (this.W * (base + 120)) / n < MIN_CELL * MIN_CELL) return this.grid(raw);
        for (const h of [base, base + 60, base + 120]) {
          const out = this.treemap(raw, h);
          if (out.groups.every((g) => g.cells.every((c) => c.w >= MIN_CELL && c.h >= MIN_CELL))) return out;
        }
      }
      return this.grid(raw);
    },
    H() {
      return this.layout.H;
    },
    groups() {
      return this.layout.groups;
    },
    cells() {
      let n = 0;
      const out = [];
      this.groups.forEach((g) => g.cells.forEach((c) => out.push({ ...c, n: n++ })));
      return out;
    },
    // Si cambian los ítems, la celda con Tab nunca queda fuera de rango
    tabStop() {
      return Math.min(this.roving, Math.max(0, this.cells.length - 1));
    },
    counts() {
      return STATUSES.map((s) => ({ status: s, n: this.items.filter((i) => i.status === s).length }));
    },
    ariaLabel() {
      const c = this.counts.map((x) => `${x.n} ${x.status.toLowerCase()}`).join(", ");
      return `Mosaico de ${this.items.length} ítems requeridos por ciclo PHVA: ${c}. Cada celda es un enlace a Estándares; usa las flechas para moverte entre celdas.`;
    },
  },
  methods: {
    fmtNum,
    treemap(raw, H) {
      const rects = squarify(raw.slice().sort((a, b) => b.value - a.value).map((g) => ({ ...g })), 0, 0, this.W, H);
      const groups = rects.map((r) => {
        const g = r.item;
        const inner = { x: r.x + GROUP_GAP / 2, y: r.y + GROUP_GAP / 2, w: r.w - GROUP_GAP, h: r.h - GROUP_GAP };
        const cells = squarify(g.items, inner.x, inner.y + LABEL_H, inner.w, inner.h - LABEL_H).map((c) => ({
          item: c.item,
          x: c.x + CELL_GAP / 2,
          y: c.y + CELL_GAP / 2,
          w: Math.max(0, c.w - CELL_GAP),
          h: Math.max(0, c.h - CELL_GAP),
        }));
        return { key: g.key, label: g.label, pts: this.groupPts(g), inner, cells };
      });
      return { H, groups };
    },
    grid(raw) {
      const cols = Math.max(1, Math.floor((this.W + GRID_GAP) / (GRID_CELL + GRID_GAP)));
      let y = 0;
      const groups = raw.map((g) => {
        const rows = Math.ceil(g.items.length / cols);
        const inner = { x: 0, y, w: this.W, h: LABEL_H + rows * (GRID_CELL + GRID_GAP) };
        const cells = g.items.map((item, k) => ({
          item,
          x: (k % cols) * (GRID_CELL + GRID_GAP),
          y: y + LABEL_H + Math.floor(k / cols) * (GRID_CELL + GRID_GAP),
          w: GRID_CELL,
          h: GRID_CELL,
        }));
        y += inner.h + GROUP_GAP;
        return { key: g.key, label: g.label, pts: this.groupPts(g), inner, cells };
      });
      return { H: Math.max(0, y - GROUP_GAP), groups };
    },
    groupPts(g) {
      return g.items.reduce((s, i) => s + (this.items.find((o) => o.id === i.id) || i).value, 0);
    },
    cls(s) {
      return "dsh-st--" + s.toLowerCase().replace(/\s+/g, "-");
    },
    showLabel(c) {
      return c.w >= 30 && c.h >= 20;
    },
    style(c) {
      return { left: c.x + "px", top: c.y + "px", width: c.w + "px", height: c.h + "px", "--i": c.n };
    },
    cellLabel(i) {
      return `${i.sequence} ${i.description}. ${i.status}. ${fmtNum(i.value)} puntos.`;
    },
    // Una sola parada de Tab para todo el mosaico; las flechas mueven el foco entre celdas.
    onFocus(c) {
      this.roving = c.n;
      this.on(c);
    },
    onKey(e, c) {
      // No pisar atajos del navegador (Alt+← atrás, Ctrl+Inicio…)
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const last = this.cells.length - 1;
      const to = {
        ArrowRight: c.n + 1,
        ArrowLeft: c.n - 1,
        ArrowDown: this.vertical(c, 1),
        ArrowUp: this.vertical(c, -1),
        Home: 0,
        End: last,
      }[e.key];
      if (to === undefined) return;
      e.preventDefault();
      const next = Math.max(0, Math.min(last, to));
      const el = this.$el.querySelectorAll(".dsh-cell")[next];
      if (el) el.focus();
    },
    // ↑/↓ van a la celda más cercana de la fila de arriba/abajo según su posición real;
    // si no hay ninguna, el foco se queda donde está.
    vertical(c, dir) {
      const cy = c.y + c.h / 2;
      const cx = c.x + c.w / 2;
      let best = c.n;
      let bestD = Infinity;
      this.cells.forEach((o) => {
        const dy = (o.y + o.h / 2 - cy) * dir;
        if (dy <= 1) return;
        const dx = o.x + o.w / 2 - cx;
        const d = dy * dy * 4 + dx * dx;
        if (d < bestD) {
          bestD = d;
          best = o.n;
        }
      });
      return best;
    },
    on(c) {
      this.active = c.item;
      this.tip = { x: c.x + c.w / 2, y: c.y };
    },
    off(c) {
      if (this.active === c.item) this.active = null;
    },
    cycleName(i) {
      const c = CYCLES.find((x) => x.key === i.cycle);
      return c ? c.label : "";
    },
    short(g) {
      return g.inner.w < 92 ? g.label : `${g.label} · ${fmtNum(g.pts)} pts`;
    },
  },
};
</script>

<template>
  <div class="dsh-mosaic" :class="{ 'is-ready': ready }">
    <div
      ref="measure"
      class="dsh-mosaic__stage"
      role="group"
      :aria-label="ariaLabel"
      :style="{ height: H + 'px' }"
    >
      <span
        v-for="g in groups"
        :key="g.key"
        class="dsh-mosaic__group"
        :style="{ left: g.inner.x + 'px', top: g.inner.y + 'px', width: g.inner.w + 'px' }"
      >{{ short(g) }}</span>
      <router-link
        v-for="c in cells"
        :key="c.item.id"
        :to="to"
        class="dsh-cell"
        :class="[cls(c.item.status), { 'is-active': active === c.item }]"
        :style="style(c)"
        :aria-label="cellLabel(c.item)"
        :tabindex="c.n === tabStop ? 0 : -1"
        @mouseenter.native="on(c)"
        @mouseleave.native="off(c)"
        @focus.native="onFocus(c)"
        @blur.native="off(c)"
        @keydown.native="onKey($event, c)"
      ><span v-if="showLabel(c)">{{ c.item.sequence }}</span></router-link>

      <DashTip :show="!!active" :x="tip.x" :y="tip.y">
        <template v-if="active">
          <div class="dsh-tip__head">{{ active.sequence }} · {{ cycleName(active) }}</div>
          <div class="dsh-tip__desc">{{ active.description }}</div>
          <div class="dsh-tip__row dsh-tip__row--key">
            <span class="dsh-sw" :class="cls(active.status)"></span>
            <strong>{{ active.status }}</strong>
            <span>· {{ fmtNum(active.value) }} {{ active.value === 1 ? "punto" : "puntos" }}</span>
          </div>
        </template>
      </DashTip>
    </div>
    <ul class="dsh-legend" aria-label="Estado de los ítems">
      <li v-for="c in counts" :key="c.status">
        <span class="dsh-sw" :class="cls(c.status)"></span>
        {{ c.status }} <strong>{{ c.n }}</strong>
      </li>
    </ul>
  </div>
</template>
